import React, { useState } from 'react'
import { styled } from 'styled-components'

// Shared styled components
export const DropIndicator = styled.div<{ $position: 'before' | 'after' }>`
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #4caf50;
  top: 0;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 10;
`

export const DraggableWrapper = styled.div<{ $isDragging: boolean }>`
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
`

// Shared layout components
export const PrimaryLineContainer = styled.div<{ $visible?: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  align-items: center;
  color: ${({ $visible }) => ($visible !== false ? 'white' : 'gray')};
  overflow: hidden;
`

export const LeftSideInfo = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  max-width: calc(100% - 80px);
  overflow: hidden;
`

export const ActionsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  position: absolute;
  right: 0;
`

// Shared drag handlers
export const useDragHandlers = <T extends { id: string }>(
  item: T,
  onReorder: (
    draggedId: string,
    targetId: string,
    position: 'before' | 'after'
  ) => void,
  dataType: string = 'item'
) => {
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | null>(
    null
  )
  const [isDragging, setIsDragging] = useState(false)

  const handleDragStart = (e: React.DragEvent) => {
    if (!onReorder) return

    e.dataTransfer.setData(dataType, item.id)
    e.dataTransfer.effectAllowed = 'move'
    setIsDragging(true)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!onReorder) return

    // Only handle if dragging the correct data type
    if (!e.dataTransfer.types.includes(dataType.toLowerCase())) {
      return
    }

    // Determine if we should drop before or after based on mouse position
    const rect = e.currentTarget.getBoundingClientRect()
    const mouseY = e.clientY - rect.top
    const position = mouseY < rect.height / 2 ? 'before' : 'after'

    setDropPosition(position)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear drop position if leaving the element
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDropPosition(null)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!onReorder) return

    const draggedId = e.dataTransfer.getData(dataType)
    if (draggedId && draggedId !== item.id && dropPosition) {
      onReorder(draggedId, item.id, dropPosition)
    }

    setDropPosition(null)
    setIsDragging(false)
  }

  const handleDragEnd = () => {
    setDropPosition(null)
    setIsDragging(false)
  }

  return {
    dropPosition,
    isDragging,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  }
}

// Shared drag props interface
export interface DragProps {
  onReorder?: (
    draggedId: string,
    targetId: string,
    position: 'before' | 'after'
  ) => void
}
